/**
 * Feature flags for the staged rollout.
 *
 * During the pilot the platform ships with a deliberately small surface:
 * sign in, home page, "about", and the on-demand video library. Everything
 * else is built and working but hidden until we switch it on — access is
 * free for any signed-in user in the meantime.
 *
 * Flip a flag to `true` to bring a feature back. That single change restores
 * its nav entry, its call-to-action buttons and its route in one go; there
 * are no other switches to find. Keep the routes registered in `main.js`
 * (the guard there reads these flags) so nothing has to be re-wired later.
 *
 * NOTE: this is presentation only. It hides affordances, it does not protect
 * anything — the API remains reachable to any authenticated caller.
 */
export const features = {
  /** Live class schedule and booking a spot (/classes). */
  classes: false,
  /** Passes: pricing on the home page, purchase and management (/passes). */
  passes: false,
  /** A user's own bookings (/bookings). */
  bookings: false,
  /** User profile and their active pass (/profile). */
  profile: false,
  /**
   * Admin panel (/admin): granting passes and managing pass types. Off along
   * with `passes` — there is nothing for it to manage while that's hidden.
   * Flip it back on together with `passes`.
   */
  admin: false,
} as const;

export type FeatureName = keyof typeof features;

export function isFeatureEnabled(name: FeatureName): boolean {
  return features[name];
}

/** Route paths that are unreachable while their feature is off. */
export const FEATURE_ROUTES: Record<string, FeatureName> = {
  "/classes": "classes",
  "/passes": "passes",
  "/bookings": "bookings",
  "/profile": "profile",
  "/admin": "admin",
};
